// Asistan marka hafızası: "kaydedildi" bildirimi, onay kartı (kampanya aksiyonları),
// yeni sohbet önerileri ve Ayarlar → Hafıza paneli. Backend: /ai-advisor/* (assistant_brain.py)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, alpha, radius, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { Button } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'Marka hafızasına kaydedildi': 'Saved to brand memory',
  'Onayla': 'Confirm',
  'İptal': 'Cancel',
  'Uygulanıyor…': 'Applying…',
  'Uygulandı': 'Applied',
  'İptal edildi': 'Cancelled',
  'Başarısız': 'Failed',
  'Öneri': 'Suggested',
  'Şimdilik desteklenmeyen hedefler': 'Destinations not supported yet',
  'Kaldığın yerden devam et': 'Pick up where you left off',
  'Yeni bir kampanya oluşturmak, durdurmak veya bütçe değiştirmek için bana yazabilirsin; her işlem önce senden onay ister.': 'Ask me to create, pause or rebudget a campaign; every change asks for your confirmation first.',
  'Hafıza': 'Memory',
  'Asistanın hatırladıkları': 'What the assistant remembers',
  'Asistan, sohbette söylediğin işletme, hedef ve kuralları bu müşteri hesabı için hatırlar. Ortak bilgiler ekipteki herkesin asistanına gider; kişisel tercihler yalnızca sana özeldir.': 'The assistant remembers the business, targets and rules you tell it, for this client account. Shared facts reach every teammate’s assistant; personal preferences stay private to you.',
  'Henüz kayıtlı bir şey yok. Asistana işletmeni, hedeflerini veya kurallarını anlattığında burada görünür.': 'Nothing saved yet. Tell the assistant about your business, targets or rules and it will appear here.',
  'Ortak': 'Shared',
  'Kişisel': 'Personal',
  'Sil': 'Delete',
  'Elle ekle': 'Add manually',
  'Bilgi Ekle': 'Add fact',
  'Kategori': 'Category',
  'Örn. Hedef ROAS 4x': 'e.g. Target ROAS 4x',
  'Yalnızca Bana Özel': 'Only for me',
  'Bu bilgiyi silmek istediğine emin misin?': 'Are you sure you want to delete this?',
  'Hafıza yüklenemedi.': 'Could not load memory.',
  'Başlat': 'Start',
  'öğe seçili': 'selected',
  'Dosya ekle (görsel / video)': 'Attach files (image / video)',
  'Yükleniyor…': 'Uploading…',
  'Dosya yüklenemedi.': 'Could not upload the file.',
  'Kaldır': 'Remove',
  'Eklenen medya': 'Attached media',
  'Kampanya': 'Campaign',
  'Reklam seti': 'Ad set',
  'Reklam': 'Ad',
});

const API = '/api/v1';

export type ActionCardData = {
  id: number; kind: string; status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled' | 'expired';
  platform: string; title: string; summary: string;
  rows: { label: string; value: string; proposed?: boolean; reason?: string }[];
  warnings: string[]; locked: { label: string; reason: string }[]; result?: { message?: string };
  tree?: TreeNode | null;
};
export type TreeNode = {
  id?: string; name: string; level: 'campaign' | 'adset' | 'ad'; meta?: string; note?: string;
  status?: string; status_label?: string; checked?: boolean; selectable?: boolean; children?: TreeNode[];
};
export type MemorySaved = { id: number; label: string; content: string; scope: string };

export type MediaItem = { id: number; kind: 'image' | 'video'; filename: string; aspect?: string; duration?: number | null; width?: number; height?: number };

/** Sohbete görsel/video ekleme: dosyalar medya kütüphanesine yüklenir, mesaja kimlikleriyle eklenir (asistan list_media ile okur). */
export function useMediaAttach(clientId: string) {
  const { t } = useLang();
  const [files, setFiles] = useState<MediaItem[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (list: FileList | null) => {
    if (!list || !list.length) return;
    setErr(null);
    for (const f of Array.from(list)) {
      setBusy(f.name);
      try {
        const fd = new FormData();
        fd.append('client_id', clientId); fd.append('file', f);
        const res = await axios.post(`${API}/ai-advisor/media`, fd, { timeout: 0, onUploadProgress: e => setBusy(`${f.name} ${e.total ? Math.round(100 * e.loaded / e.total) : ''}%`) });
        setFiles(prev => [...prev, res.data]);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || t('Dosya yüklenemedi.'));
      }
    }
    setBusy(null);
  };
  const remove = (id: number) => {
    setFiles(prev => prev.filter(m => m.id !== id));
    axios.delete(`${API}/ai-advisor/media/${id}?client_id=${clientId}`).catch(() => {});
  };
  const suffix = () => files.length
    ? '\n[Eklenen medya] ' + files.map(m => `#${m.id} ${m.filename} (${m.kind === 'video' ? 'video' : 'görsel'}${m.aspect ? ' ' + m.aspect : ''}${m.duration ? ', ' + Math.round(m.duration) + ' sn' : ''})`).join('; ')
    : '';
  return { files, busy, err, pick, remove, clear: () => setFiles([]), suffix };
}

function Thumb({ id, clientId }: { id: number; clientId: string }) {
  const [src, setSrc] = useState<string>('');
  useEffect(() => {
    let url = '';
    axios.get(`${API}/ai-advisor/media/${id}/thumb?client_id=${clientId}`, { responseType: 'blob' }).then(r => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, clientId]);
  return src ? <img src={src} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} /> : <span style={{ width: 28, height: 28, borderRadius: 6, background: T.bg, display: 'inline-block' }} />;
}

export function MediaChips({ attach, clientId }: { attach: ReturnType<typeof useMediaAttach>; clientId: string }) {
  const { t } = useLang();
  if (!attach.files.length && !attach.busy && !attach.err) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
      {attach.files.map(m => (
        <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, borderRadius: 10, padding: '3px 8px 3px 3px', background: T.surface, fontSize: 11.5, color: T.text, maxWidth: 220 }}>
          <Thumb id={m.id} clientId={clientId} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.filename}</span>
          <span style={{ color: T.textDim }}>{m.aspect}</span>
          <span title={t('Kaldır')} onClick={() => attach.remove(m.id)} style={{ cursor: 'pointer', color: T.textDim, fontWeight: 800, padding: '0 2px' }}>×</span>
        </span>
      ))}
      {attach.busy && <span style={{ fontSize: 11.5, color: T.textDim, alignSelf: 'center' }}>{t('Yükleniyor…')} {attach.busy}</span>}
      {attach.err && <span style={{ fontSize: 11.5, color: T.redText, alignSelf: 'center' }}>{attach.err}</span>}
    </div>
  );
}

export function AttachButton({ attach, size = 30 }: { attach: ReturnType<typeof useMediaAttach>; size?: number }) {
  const { t } = useLang();
  const ref = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" multiple accept="image/jpeg,image/png,video/mp4,video/quicktime,.jpg,.jpeg,.png,.mp4,.mov" style={{ display: 'none' }}
        onChange={e => { attach.pick(e.target.files); e.target.value = ''; }} />
      <button type="button" title={t('Dosya ekle (görsel / video)')} onClick={() => ref.current?.click()} disabled={!!attach.busy}
        style={{ width: size, height: size, borderRadius: '50%', border: 'none', background: 'transparent', color: T.textMid, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
        <Icon.paperclip size={size > 32 ? 19 : 17} />
      </button>
    </>
  );
}


export function MemorySavedNote({ items }: { items?: MemorySaved[] }) {
  const { t } = useLang();
  if (!items?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
      {items.map(m => (
        <div key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start', maxWidth: '100%',
          border: `1px solid ${T.border}`, borderRadius: 999, padding: '6px 12px 6px 8px', background: T.surface, fontSize: 12.5, color: T.text }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%', background: T.greenTint, color: T.greenText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.check size={13} sw={2.2} />
          </span>
          <span style={{ fontWeight: 700 }}>{t('Marka hafızasına kaydedildi')}</span>
          <span style={{ color: T.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {m.content}</span>
        </div>
      ))}
    </div>
  );
}


const LEVEL_COLOR: Record<string, string> = { campaign: T.accent, adset: T.green, ad: T.textDim };

function statusTone(st?: string) {
  if (st === 'ACTIVE') return { bg: T.greenTint, fg: T.greenText };
  if (st === 'PENDING_REVIEW' || st === 'IN_PROCESS') return { bg: T.amberTint, fg: T.amberDeepText };
  if (st === 'DISAPPROVED' || st === 'WITH_ISSUES') return { bg: T.redTint, fg: T.redText };
  return { bg: T.bg, fg: T.textDim };
}

function collectIds(n: TreeNode, out: string[] = []): string[] {
  if (n.id) out.push(n.id);
  (n.children || []).forEach(c => collectIds(c, out));
  return out;
}
function initialSelection(n: TreeNode, out: Set<string> = new Set()): Set<string> {
  if (n.id && n.checked) out.add(n.id);
  (n.children || []).forEach(c => initialSelection(c, out));
  return out;
}

function TreeRow({ node, depth, selectable, sel, toggle, locked }: { node: TreeNode; depth: number; selectable: boolean; sel: Set<string>;
  toggle: (n: TreeNode, ancestors: TreeNode[]) => void; locked: boolean }) {
  const { t } = useLang();
  return <TreeRows node={node} depth={depth} selectable={selectable} sel={sel} toggle={toggle} locked={locked} ancestors={[]} tt={t} />;
}

function TreeRows({ node, depth, selectable, sel, toggle, locked, ancestors, tt }: { node: TreeNode; depth: number; selectable: boolean; sel: Set<string>;
  toggle: (n: TreeNode, ancestors: TreeNode[]) => void; locked: boolean; ancestors: TreeNode[]; tt: (s: string) => string }) {
  const on = !!node.id && sel.has(node.id);
  const tone = statusTone(node.status);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', marginLeft: depth * 16, borderLeft: depth ? `2px solid ${T.divider}` : 'none', paddingLeft: depth ? 10 : 0 }}>
        {selectable && node.id && (
          <input type="checkbox" checked={on} disabled={locked} onChange={() => toggle(node, ancestors)} style={{ marginTop: 3, accentColor: T.accent, cursor: locked ? 'default' : 'pointer' }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.4, wordBreak: 'break-word' }}>
            <span style={{ fontWeight: 700 }}>{node.name}</span>
            <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: LEVEL_COLOR[node.level] || T.textDim, textTransform: 'uppercase', letterSpacing: '.04em' }}>{tt(node.level === 'campaign' ? 'Kampanya' : node.level === 'adset' ? 'Reklam seti' : 'Reklam')}</span>
            {node.status_label && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: tone.fg, background: tone.bg, borderRadius: 999, padding: '1px 8px' }}>{node.status_label}</span>}
          </div>
          {node.meta && <div style={{ fontSize: 11.5, color: T.textDim, lineHeight: 1.4, marginTop: 1 }}>{node.meta}</div>}
          {node.note && <div style={{ fontSize: 11.5, color: T.amberDeepText, lineHeight: 1.4, marginTop: 1 }}>{node.note}</div>}
        </div>
      </div>
      {(node.children || []).map((c, i) => (
        <TreeRows key={(c.id || '') + i} node={c} depth={depth + 1} selectable={selectable} sel={sel} toggle={toggle} locked={locked} ancestors={[...ancestors, node]} tt={tt} />
      ))}
    </>
  );
}

export function ActionCard({ card, onChange }: { card: ActionCardData; onChange: (c: ActionCardData) => void }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selectable = !!card.tree?.selectable;
  const [sel, setSel] = useState<Set<string>>(() => (card.tree && card.tree.selectable ? initialSelection(card.tree) : new Set()));
  const toggle = (n: TreeNode, ancestors: TreeNode[]) => {
    setSel(prev => {
      const next = new Set(prev);
      const ids = collectIds(n);
      if (n.id && prev.has(n.id)) ids.forEach(i => next.delete(i));
      else { ids.forEach(i => next.add(i)); ancestors.forEach(a => a.id && next.add(a.id)); }   // alt öğe seçilince üstleri de gerekir
      return next;
    });
  };
  const call = async (path: 'confirm' | 'cancel') => {
    setBusy(true); setErr(null);
    try {
      const res = await axios.post(`${API}/ai-advisor/actions/${card.id}/${path}`, path === 'confirm' && selectable ? { selected: Array.from(sel) } : undefined);
      onChange(res.data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t('Bir hata oluştu, tekrar dener misin?'));
    } finally { setBusy(false); }
  };
  const done = card.status === 'done', failed = card.status === 'failed' || card.status === 'expired', cancelled = card.status === 'cancelled';
  const tone = done ? T.green : failed ? T.red : T.accent;
  return (
    <div style={{ marginTop: 14, border: `1px solid ${alpha(tone, 45)}`, borderRadius: radius.lg, background: T.surface, overflow: 'hidden', maxWidth: card.tree ? 680 : 520 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: tint(tone, 10), borderBottom: `1px solid ${T.divider}` }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: tint(tone, 22), color: done ? T.greenText : failed ? T.redText : T.amberDeepText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {done ? <Icon.check size={15} sw={2.2} /> : failed ? <Icon.warningTriangle size={14} sw={2} /> : <Icon.bolt size={14} sw={2} />}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: T.text, flex: 1, minWidth: 0 }}>{card.title}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: done ? T.greenText : failed ? T.redText : T.textDim }}>
          {done ? t('Uygulandı') : cancelled ? t('İptal edildi') : failed ? t('Başarısız') : ''}
        </span>
      </div>
      <div style={{ padding: '10px 14px' }}>
        {card.rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, justifyContent: 'space-between', padding: '6px 0', borderBottom: i < card.rows.length - 1 ? `1px solid ${T.divider}` : 'none', fontSize: 12.5 }}>
            <span style={{ color: T.textDim, flexShrink: 0 }}>{r.label}</span>
            <span style={{ color: T.text, fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>
              {r.value}
              {r.proposed && (
                <span title={r.reason} style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: T.amberDeepText, background: T.amberTint, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{t('Öneri')}</span>
              )}
              {r.proposed && r.reason && <div style={{ fontSize: 11, fontWeight: 500, color: T.textDim, marginTop: 2 }}>{r.reason}</div>}
            </span>
          </div>
        ))}
        {card.tree && (
          <div style={{ marginTop: card.rows.length ? 10 : 0, padding: '8px 10px', background: T.bg, borderRadius: 10 }}>
            <TreeRow node={card.tree} depth={0} selectable={selectable && card.status === 'pending'} sel={sel} toggle={toggle} locked={busy} />
            {selectable && card.status === 'pending' && <div style={{ marginTop: 6, fontSize: 11.5, color: T.textDim }}>{sel.size} {t('öğe seçili')}</div>}
          </div>
        )}
        {card.locked?.length > 0 && card.status === 'pending' && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: T.textDim, lineHeight: 1.5 }}>
            <b style={{ color: T.textMid }}>{t('Şimdilik desteklenmeyen hedefler')}:</b> {card.locked.map(l => l.label).join(', ')}
          </div>
        )}
        {card.status === 'pending' && card.warnings.map((w, i) => (
          <div key={i} style={{ marginTop: 8, fontSize: 11.5, color: T.amberDeepText, background: T.amberTint, borderRadius: 8, padding: '6px 10px', lineHeight: 1.45 }}>{w}</div>
        ))}
        {(card.result?.message || err) && (
          <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: err || failed ? T.redText : T.greenText, lineHeight: 1.5 }}>{err || card.result?.message}</div>
        )}
      </div>
      {card.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8, padding: '0 14px 14px' }}>
          <Button variant="solid" disabled={busy || (selectable && sel.size === 0)} onClick={() => call('confirm')}>{busy ? t('Uygulanıyor…') : t(selectable ? 'Başlat' : 'Onayla')}</Button>
          <Button variant="ghost" disabled={busy} onClick={() => call('cancel')}>{t('İptal')}</Button>
        </div>
      )}
    </div>
  );
}

export type Suggestion = { id: string; label: string; sub: string; question: string; mode: 'send' | 'fill'; platform: string };

export function useSuggestions(clientId: string) {
  const [items, setItems] = useState<Suggestion[]>([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/ai-advisor/suggestions?client_id=${clientId}`)
      .then(r => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [clientId]);
  return items;
}

export function SuggestionList({ items, onPick, compact }: { items: Suggestion[]; onPick: (s: Suggestion) => void; compact?: boolean }) {
  const { t } = useLang();
  if (!items.length) return null;
  return (
    <div style={{ width: '100%', maxWidth: 640, marginTop: compact ? 16 : 26 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: T.textDim, marginBottom: compact ? 6 : 10, textAlign: 'left' }}>{t('Kaldığın yerden devam et')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
        {items.map(s => (
          <button key={s.id} onClick={() => onPick(s)} style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', width: '100%',
            border: `1px solid ${T.border}`, borderRadius: 12, padding: compact ? '8px 12px' : '11px 14px', background: T.surface, cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ width: 28, height: 28, borderRadius: 9, background: T.amberTint, color: T.amberDeepText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon.chevronRight size={15} sw={2.2} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
              {s.sub && <span style={{ display: 'block', fontSize: 11.5, color: T.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.sub}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Ayarlar → Hafıza ────────────────────────────────────────────────────────
type MemItem = { id: number; category: string; label: string; content: string; scope: 'shared' | 'personal'; mine: boolean };

export function BrandMemoryPanel({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const [items, setItems] = useState<MemItem[]>([]);
  const [cats, setCats] = useState<Record<string, string>>({});
  const [canShared, setCanShared] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cat, setCat] = useState('goals');
  const [text, setText] = useState('');
  const [personal, setPersonal] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true); setError('');
    axios.get(`${API}/ai-advisor/memory?client_id=${clientId}`)
      .then(r => { setItems(r.data.items || []); setCats(r.data.categories || {}); setCanShared(!!r.data.can_write_shared); })
      .catch(e => setError(e?.response?.data?.detail || t('Hafıza yüklenemedi.')))
      .finally(() => setLoading(false));
  };
  useEffect(load, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/ai-advisor/memory`, { client_id: clientId, category: cat, content: text, scope: personal || !canShared || cat === 'preferences' ? 'personal' : 'shared' });
      setText(''); load();
    } catch (e: any) { setError(e?.response?.data?.detail || t('Bir hata oluştu, tekrar dener misin?')); }
    finally { setSaving(false); }
  };
  const del = async (id: number) => {
    if (!window.confirm(t('Bu bilgiyi silmek istediğine emin misin?'))) return;
    try { await axios.delete(`${API}/ai-advisor/memory/${id}?client_id=${clientId}`); setItems(xs => xs.filter(x => x.id !== id)); }
    catch (e: any) { setError(e?.response?.data?.detail || t('Bir hata oluştu, tekrar dener misin?')); }
  };

  const grouped = Object.keys(cats).map(k => ({ key: k, label: cats[k], list: items.filter(i => i.category === k) })).filter(g => g.list.length);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 6 }}>{t('Asistanın hatırladıkları')}</div>
        <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>{t('Asistan, sohbette söylediğin işletme, hedef ve kuralları bu müşteri hesabı için hatırlar. Ortak bilgiler ekipteki herkesin asistanına gider; kişisel tercihler yalnızca sana özeldir.')}</div>
      </div>

      <div style={{ border: `1px solid ${T.border}`, borderRadius: radius.lg, background: T.surface, padding: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select value={cat} onChange={e => setCat(e.target.value)} aria-label={t('Kategori')}
          style={{ padding: '9px 10px', borderRadius: 10, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 13 }}>
          {Object.keys(cats).map(k => <option key={k} value={k}>{cats[k]}</option>)}
        </select>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} maxLength={300} placeholder={t('Örn. Hedef ROAS 4x')}
          style={{ flex: '1 1 220px', minWidth: 0, padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
        {canShared && cat !== 'preferences' && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMid, cursor: 'pointer' }}>
            <input type="checkbox" checked={personal} onChange={e => setPersonal(e.target.checked)} /> {t('Yalnızca Bana Özel')}
          </label>
        )}
        <Button variant="solid" disabled={saving || !text.trim()} onClick={add}>{t('Bilgi Ekle')}</Button>
      </div>

      {error && <div style={{ fontSize: 12.5, color: T.redText }}>{error}</div>}
      {loading ? <div style={{ fontSize: 13, color: T.textDim }}>…</div> : grouped.length === 0 ? (
        <div style={{ border: `1px dashed ${T.borderMid}`, borderRadius: radius.lg, padding: 28, textAlign: 'center', fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>
          {t('Henüz kayıtlı bir şey yok. Asistana işletmeni, hedeflerini veya kurallarını anlattığında burada görünür.')}
        </div>
      ) : grouped.map(g => (
        <div key={g.key} style={{ border: `1px solid ${T.border}`, borderRadius: radius.lg, background: T.surface, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: T.textDim, background: T.subtleBg }}>{g.label}</div>
          {g.list.map((it, i) => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: i ? `1px solid ${T.divider}` : 'none' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: T.text, lineHeight: 1.5, wordBreak: 'break-word' }}>{it.content}</span>
              <span style={{ fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap',
                color: it.scope === 'shared' ? T.blueText : T.purpleText, background: it.scope === 'shared' ? T.blueTint : T.purpleTint }}>{it.scope === 'shared' ? t('Ortak') : t('Kişisel')}</span>
              {(it.scope === 'personal' ? it.mine : canShared) && (
                <button onClick={() => del(it.id)} title={t('Sil')} aria-label={t('Sil')} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: T.textDim, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon.trash size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
